import React from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { Sparkles } from 'lucide-react';
import { BorderBeam } from 'border-beam';
import { useReducedMotion } from 'framer-motion';

import { cn } from '@/lib/utils';
import { Button } from '@/components/ui/button';
import { useSidebar } from '@/components/ui/sidebar';
import { useCreditBalance } from '../hooks/use-credits.hook';
import { useVendorPlan } from '../hooks/use-vendor-plan.hook';
import { PlanChip } from '../../components/design-system/plan/PlanChip';
import { UPGRADE_PATH, upgradeHref } from '../../components/design-system/plan/plan-limits';

/**
 * What is left to spend on AI, at the foot of the rail.
 *
 * Above the account block rather than in the top bar: it belongs with the
 * things about the account rather than with the things you do, and the top bar
 * is already four controls wide on a laptop.
 *
 * Everything the product does with a model is metered against this — the
 * assistant, categorisation, the analyst, email copy, images — so a vendor who
 * cannot see it finds out they have run out by being refused. It reloads itself
 * whenever an agent run finishes, which is the only moment it changes.
 *
 * On the Free plan it also carries the plan itself: a "Free plan" chip whose
 * popover lists what is left this period, and the monthly allowance (30) as
 * the denominator — the credits reset each month, and the way to more is the
 * Upgrade button under them.
 */
export const SidebarCredits: React.FC = () => {
    const { t, i18n } = useTranslation();
    const navigate = useNavigate();
    const { balance, loading } = useCreditBalance();
    const { limits, isLimitDisabled, isFree } = useVendorPlan();
    const { state } = useSidebar();
    const reduceMotion = useReducedMotion();

    // Nothing rather than a zero we are not sure about: a failed request must
    // not read as an empty wallet. A Free vendor still sees which plan they are on.
    if (loading || balance === null) {
        return isFree && state !== 'collapsed' ? (
            <div className="px-1 pb-1">
                <PlanChip />
            </div>
        ) : null;
    }

    const safe = Math.max(0, balance);
    const allowance = limits?.monthlyCredits ?? -1;
    // -1 is how the plan says "no ceiling", so there is no denominator to show.
    const metered = allowance > 0;
    const empty = safe <= 0;
    const low = !empty && (metered ? safe / allowance <= 0.15 : safe <= 25);

    const fmt = (v: number) => new Intl.NumberFormat(i18n.language).format(v);

    /*
     * Collapsed to just the figure when the rail is.
     *
     * A card with a heading, a sentence and a button inside a 48px rail is
     * three ellipses stacked on each other. The number is the part worth
     * keeping, and the full card is one click away.
     */
    if (state === 'collapsed') {
        return (
            <button
                type="button"
                onClick={() => navigate(upgradeHref(empty ? 'monthlyCredits' : undefined))}
                title={t('SIDEBAR.CREDITS.LEFT', { total: fmt(safe) }) as string}
                className={cn(
                    'mx-auto flex w-full flex-col items-center gap-0.5 rounded-lg py-2 text-[11px] tabular-nums transition-colors hover:bg-accent',
                    empty ? 'text-destructive' : low ? 'text-amber-600 dark:text-amber-500' : 'text-muted-foreground',
                )}
            >
                <Sparkles className="size-3.5" />
                {new Intl.NumberFormat(i18n.language, { notation: 'compact', maximumFractionDigits: 1 }).format(safe)}
            </button>
        );
    }

    return (
        /*
         * The same travelling beam as the Generate button in the studio.
         * `size="line"` is its bottom-only glow, which is the edge the depth
         * was wanted on.
         *
         * Slower and weaker than the button's, though. There it is an
         * affordance — this is ready, press it — and it is on screen while you
         * look at one recipe. This sits in the corner of the eye on every page
         * all day, and at button speed a permanent animation stops reading as
         * polish. Off entirely for anyone who asked for less motion.
         */
        <BorderBeam size="line" colorVariant="ocean" duration={6} strength={0.45} active={!reduceMotion}>
            <div className="rounded-xl border bg-card p-3" data-testid="sidebar-credits">
                <div className="flex items-center justify-between gap-2">
                    <p
                        className={cn(
                            'flex items-center gap-1.5 text-sm tabular-nums',
                            empty && 'text-destructive',
                            low && 'text-amber-600 dark:text-amber-500',
                        )}
                    >
                        <Sparkles className="size-3.5 shrink-0" />
                        {metered ? `${fmt(safe)}/${fmt(allowance)}` : fmt(safe)}
                    </p>
                    {isFree && <PlanChip align="end" />}
                </div>
                <p className="mt-1 text-xs leading-relaxed text-muted-foreground">
                    {t(empty ? 'SIDEBAR.CREDITS.EMPTY' : low ? 'SIDEBAR.CREDITS.LOW' : 'SIDEBAR.CREDITS.HINT')}
                    {isFree && metered && !empty && !low && <span className="block">{t('FREE_PLAN.METER.RESETS')}</span>}
                </p>
                {/* Outlined, never filled: a solid button at the foot of every
                    page would outrank the vendor's actual work. */}
                {!isLimitDisabled('monthlyCredits') && (
                    <Button
                        variant="outline"
                        size="sm"
                        className="mt-2.5 w-full"
                        onClick={() => navigate(empty || low ? upgradeHref('monthlyCredits') : UPGRADE_PATH)}
                    >
                        {t('SIDEBAR.CREDITS.UPGRADE')}
                    </Button>
                )}
            </div>
        </BorderBeam>
    );
};
